import Slider, { Handle } from 'rc-slider';
import { Component, createRef } from 'react';
import styled from 'styled-components';

import { FormItem, ValidationState } from '../Form/FormItem';

const RangeSliderElement = styled.div<ICommonProps>`
  ${props => props.theme.partials.form.item.default};
  .rc-slider {
    position: relative;
    height: 20px;
    padding: 5px 0;
    margin-right: 30px;
    margin-left: 15px;
    width: 100%;
    touch-action: none;
    box-sizing: border-box;
  }
  .rc-slider-rail {
    position: absolute;
    width: 100%;
    background-color: ${({ theme }) => theme.vars.newColor.darkGS2};
    height: 10px;
  }
  .rc-slider-track {
    position: absolute;
    left: 0;
    height: 10px;
    background-color: ${({ theme }) => theme.vars.newColor.green1};
  }
  .rc-slider-handle {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: -10px;
    width: 30px;
    height: 30px;
    cursor: grab;
    border-radius: 50%;
    box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    background-color: #fff;
    touch-action: pan-x;
    :focus {
      outline: none;
    }
    :active {
      cursor: grabbing;
    }
    i {
      background-color: ${({ theme }) => theme.vars.newColor.lightGS1};
      display: block;
      height: 10px;
      width: 1px;
      margin: 0 2px;
    }
  }

  .rc-slider-step {
    position: absolute;
    width: 100%;
    height: 10px;
    background: transparent;
  }

  .slider-value {
    margin-right: 15px;
    min-width: 45px;
    text-align: center;
    color: ${props => (props.disabled ? props.theme.vars.newColor.darkGS3 : 'inherit')};
  }

  .rc-slider-disabled {
    .rc-slider-rail {
      background-color: ${({ theme }) => theme.vars.newColor.lightGS1};
    }
    .rc-slider-track {
      background-color: transparent;
    }
    .rc-slider-handle {
      cursor: not-allowed;
    }
  }
`;

interface ICommonProps {
  /** Specifies whether the Range Slider is disabled  */
  disabled?: boolean;
}

export interface IProps extends Partial<ICommonProps> {
  /** Range Slider label */
  label?: string;
  /** Range Slider value */
  value: number;
  /** Adds a prefix to the value displayed */
  prefix?: string;
  /** Adds a suffix to the value displayed */
  suffix?: string;
  /** Min Range Slider value (default: 0) */
  min?: number;
  /** Max Range Slider value (default: 100) */
  max?: number;
  /** Range Slider tabindex */
  tabIndex?: number;
  /** Automatically focus Range Slider when page loads */
  autoFocus?: boolean;
  /** Invoke onChange of Range Slider  */
  onChange?: (value?: number) => void;
  /** Range Slider validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Range Slider validation message  */
  validationMessage?: string;
  /** Adds spacing below the Range Slider (default: true) */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the Range Slider is used for */
  hint?: any;
  /** Optional tooltip heading. Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description. Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
}

export class RangeSlider extends Component<IProps> {
  private sliderHandleRef: React.RefObject<any>;
  public constructor(props: IProps) {
    super(props);
    this.sliderHandleRef = createRef();
  }

  public componentDidMount() {
    if (this.props.autoFocus) {
      this.sliderHandleRef.current.focus();
    }
  }
  public render() {
    return (
      <FormItem
        label={this.props.label}
        validationMessage={this.props.validationMessage}
        validationState={this.props.validationState}
        spacingBelow={this.props.spacingBelow}
        hint={this.props.hint}
        tooltipHeading={this.props.tooltipHeading}
        tooltipDescription={this.props.tooltipDescription}
        tooltipWidth={this.props.tooltipWidth}
      >
        <RangeSliderElement data-testid="SliderContainer" disabled={this.props.disabled}>
          <Slider
            handle={sliderProps => this.CustomSliderHandle(sliderProps)}
            onChange={this.handleSliderChange}
            value={this.props.value}
            min={this.props.min}
            max={this.props.max}
            ref={this.sliderHandleRef}
            tabIndex={this.props.tabIndex}
            disabled={this.props.disabled}
          />
          <div className="slider-value">
            {this.props.prefix ? this.props.prefix : null}
            {this.props.value}
            {this.props.suffix ? this.props.suffix : null}
          </div>
        </RangeSliderElement>
      </FormItem>
    );
  }

  private handleSliderChange = (e: number) => {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  private CustomSliderHandle = (sliderProps: any) => {
    const { dragging, ...additionalSliderProps } = sliderProps;
    return (
      <Handle dragging={sliderProps.dragging.toString()} {...additionalSliderProps} data-testid="SliderHandle">
        <i />
        <i />
        <i />
      </Handle>
    );
  };
}
